/**
 * RunFlagsBar — every flag on the open run, one chip each (TRA-1088).
 *
 * A run can carry a flag per request (dreamer findings on two requests,
 * or a reviewer's own second flag). Each chip names the request the flag
 * points at and shows its own verdict; clicking one opens that flag, so
 * the reviewer settles the run flag by flag without leaving it. The menu
 * on the right lists the requests that carry no flag yet: picking one
 * opens it for review, and saving creates the flag (the same stand-in
 * path a Trajectories link uses, TRA-1079).
 */

import { ChevronDown } from 'lucide-react'

import { flagSourceLabel, rowVerdict } from '@/components/review/flagSource'
import type { FlaggableRequest, RunFlag } from '@/components/review/runFlags'
import { VerdictChip } from '@/components/review/ReviewQueueSidebar'
import { isAdHocItem } from '@/components/review/adHocItem'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface RunFlagsBarProps {
  flags: RunFlag[]
  selectedTraceId: string
  onSelect: (traceId: string) => void
  /** Requests without a flag; empty while the transcript loads. */
  requests: FlaggableRequest[]
  onFlagRequest: (request: FlaggableRequest) => void
}

function requestLabel(flag: RunFlag): string {
  if (flag.request === null) return 'request not shown'
  return `request ${flag.request.toString()}`
}

export function RunFlagsBar({
  flags,
  selectedTraceId,
  onSelect,
  requests,
  onFlagRequest,
}: RunFlagsBarProps): React.JSX.Element {
  const count = flags.filter((f) => !isAdHocItem(f.item)).length
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 border-b border-border px-8 py-2 text-xs text-muted-foreground">
      <span className="mr-1 font-mono text-[11px] uppercase tracking-wider">
        {count === 0
          ? 'No flags on this run yet'
          : count === 1
            ? '1 flag on this run'
            : `${count.toString()} flags on this run`}
      </span>
      {flags.map((flag) => {
        const current = flag.item.trace_id === selectedTraceId
        const verdict = rowVerdict(flag.item)
        const adHoc = isAdHocItem(flag.item)
        return (
          <button
            key={flag.item.trace_id}
            type="button"
            aria-current={current}
            onClick={() => {
              onSelect(flag.item.trace_id)
            }}
            className={`inline-flex min-h-7 items-center gap-1.5 rounded-md border px-2 ${
              current
                ? 'border-muted-foreground bg-muted text-foreground'
                : 'border-border hover:bg-muted/60 hover:text-foreground'
            }`}
          >
            <span className="font-mono tabular-nums">{requestLabel(flag)}</span>
            <span>·</span>
            <span>{adHoc ? 'new flag' : flagSourceLabel(flag.item)}</span>
            {verdict !== null && <VerdictChip {...verdict} />}
          </button>
        )
      })}
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={requests.length === 0}
          className="ml-auto inline-flex min-h-7 items-center gap-1 rounded-md px-2 hover:bg-muted/60 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          title={
            requests.length === 0
              ? 'Every request of this run already has a flag'
              : 'Open a request of this run to add a flag on it'
          }
        >
          Flag another request
          <ChevronDown aria-hidden="true" className="h-3 w-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
          {requests.map((request) => (
            <DropdownMenuItem
              key={request.traceId}
              onSelect={() => {
                onFlagRequest(request)
              }}
            >
              request {request.n}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
